import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { MessageCircle, X, Send, Loader2, Sparkles, BookOpen, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Discovery chips shown on first open. Each tap pre-fills the input so
// users new to the app can learn what the assistant actually covers —
// and reinforces that the assistant answers PLATFORM questions only.
const QUICK_PROMPTS = [
  'How do I create a new schedule?',
  'How do I run live stats for basketball?',
  'How do I export a team roster?',
  'How does Regenerate work?',
  'How do I invite a collaborator?',
  "What's the difference between Free and Pro?",
];

/**
 * Floating virtual assistant.
 *
 * Renders a small chat bubble in the bottom-right on every logged-in page.
 * Clicking it opens a panel that talks to the Schedule Guru knowledge base
 * via /api/assistant/chat. Scope is strictly platform-feature help —
 * off-topic prompts are refused server-side via the system prompt.
 * Not shown for anonymous users so we don't leak the LLM key budget.
 */
export function AssistantWidget() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  // Hide on Live Stats tracker pages — the courtside statistician needs the
  // full screen for stat buttons, not an overlapping chat bubble.
  const isLiveStatsRoute = /^\/stats\/game(\/|$)/.test(location.pathname);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading]);

  // Seed the first-open with a welcome message that scopes the assistant.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content:
          "Hi — I'm the Schedule Guru platform assistant. I can answer questions about using the app: creating schedules, running live stats, managing rosters, exports, pricing, and the like.\n\nTry one of the suggestions below, or ask your own question.",
      }]);
    }
  }, [open, messages.length]);

  if (!isAuthenticated) return null;
  if (isLiveStatsRoute) return null;

  // Reset to the welcome state so the user can start a fresh question
  // from the quick-prompt chips. Keeps the server-side session_id so
  // follow-up questions after a reset still share conversation history,
  // but the visible chat is cleared and the welcome + quick prompts
  // return — matching the "Back to Start" UX ask.
  const resetConversation = () => {
    setMessages([]);
    setInput('');
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
          session_id: sessionId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Assistant is offline');
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply || '' }]);
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Sorry — I ran into an error: ${e.message}. Please try again in a moment.`,
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#00247D] hover:bg-[#001A5C] shadow-lg flex items-center justify-center text-white transition-transform hover:scale-105 print:hidden"
          aria-label="Open Schedule Guru assistant"
          data-testid="assistant-open-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <Card
          className="fixed bottom-6 right-6 z-40 w-[90vw] max-w-sm shadow-2xl border-slate-200 print:hidden"
          data-testid="assistant-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#00247D] text-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <div>
                <p className="text-sm font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                  SCHEDULE GURU ASSISTANT
                </p>
                <p className="text-[11px] text-white/70">Platform questions only</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Link
                to={location.pathname.startsWith('/officials') ? '/manual/officials' : '/help'}
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white p-1 flex items-center gap-1 text-[11px]"
                data-testid="assistant-manual-link"
                title="Open the user manual"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Manual
              </Link>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white p-1"
                aria-label="Close assistant"
                data-testid="assistant-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <CardContent className="p-0">
            <div
              ref={listRef}
              className="max-h-[420px] min-h-[260px] overflow-y-auto px-4 py-3 space-y-3 bg-slate-50"
              data-testid="assistant-message-list"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`assistant-msg-${m.role}-${i}`}
                >
                  <div
                    className={`whitespace-pre-wrap rounded-lg px-3 py-2 text-sm max-w-[85%] leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-[#00247D] text-white rounded-br-sm'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {/* Quick-prompt chips — rendered only when the conversation
                  is still at the welcome message so first-time users can
                  one-tap into the most common questions. */}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="assistant-quick-prompts">
                  {QUICK_PROMPTS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      className="text-[11px] bg-white hover:bg-[#00247D] hover:text-white hover:border-[#00247D] text-slate-700 border border-slate-300 rounded-full px-2.5 py-1 transition-colors"
                      data-testid={`assistant-quick-${q.slice(0, 20).replace(/\W+/g, '-').toLowerCase()}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {loading && (
                <div className="flex justify-start" data-testid="assistant-loading">
                  <div className="bg-white text-slate-500 border border-slate-200 rounded-lg rounded-bl-sm px-3 py-2 text-sm inline-flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Thinking…
                  </div>
                </div>
              )}
              {/* Back to Start — surfaces once the assistant has replied
                  to at least one user question. Resets the pane to the
                  welcome message + quick-prompt chips so the user can
                  one-tap into a different topic. Hidden during loading. */}
              {!loading && messages.filter(m => m.role === 'user').length > 0 && (
                <div className="flex flex-col items-center gap-1.5 pt-1" data-testid="assistant-back-to-start-row">
                  <button
                    type="button"
                    onClick={resetConversation}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#00247D] bg-white border border-[#00247D]/30 hover:bg-[#00247D] hover:text-white rounded-full px-3 py-1.5 transition-colors"
                    data-testid="assistant-back-to-start"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Back to Start
                  </button>
                  {/* Human-escalation link. Pre-fills a new support ticket
                      with the last question so the user doesn't retype. */}
                  <Link
                    to={`/support/new?from=assistant&q=${encodeURIComponent(messages.filter(m => m.role === 'user').slice(-1)[0]?.content || '')}`}
                    onClick={() => setOpen(false)}
                    className="text-[11px] text-slate-500 hover:text-[#00247D] underline underline-offset-2"
                    data-testid="assistant-escalate-link"
                  >
                    Still need human help? Open a support ticket
                  </Link>
                </div>
              )}
            </div>

            <form
              className="flex items-center gap-2 border-t border-slate-100 p-3 bg-white rounded-b-lg"
              onSubmit={(e) => { e.preventDefault(); send(); }}
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about a Schedule Guru feature…"
                disabled={loading}
                maxLength={1500}
                data-testid="assistant-input"
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-[#00247D] hover:bg-[#001A5C]"
                data-testid="assistant-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </>
  );
}
